import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronDown, Copy } from 'lucide-react';
import { estimatesService } from '@/services/estimates-service';

/** Format a numeric string with commas (e.g., "12000" → "12,000") */
function fmtWithCommas(raw: string): string {
  if (!raw) return raw;
  const stripped = raw.replace(/,/g, '');
  const num = parseFloat(stripped);
  if (isNaN(num)) return raw;
  // Preserve decimal portion exactly as typed
  const parts = stripped.split('.');
  const intFormatted = parts[0] === '' ? '0' : Math.abs(parseInt(parts[0], 10)).toLocaleString('en-US');
  const sign = stripped.startsWith('-') ? '-' : '';
  return parts.length > 1 ? `${sign}${intFormatted}.${parts[1]}` : `${sign}${intFormatted}`;
}

/** Strip commas from input for storage (e.g., "12,000" → "12000") */
function stripCommas(val: string): string {
  return val.replace(/,/g, '');
}

export interface WriteupField {
  value: string;
  size: 'sm' | 'md' | 'lg';
  placeholder?: string;
  options?: { label: string; value: string }[]; // If present, renders a <select> dropdown
}

export interface WriteupRow {
  id: string;
  content: (string | WriteupField)[];
  enabled: boolean;
}

export interface WriteupNote {
  id: string;
  content: string;
}

export interface WriteupSection {
  id: string;
  title: string;
  rows: WriteupRow[];
  note: WriteupNote;
  collapsed: boolean;
}

export interface WriteupGroup {
  id: string;
  title: string;
  rows: WriteupRow[];
  sections: WriteupSection[];
  collapsed: boolean;
  note?: WriteupNote;
}

interface WriteupPanelProps {
  estimateId: string;
  projectTypes?: string[];
  initialScopeData?: string; // JSON-serialized WriteupGroup[] from DB
  onScopeChange?: (scopeData: string) => void; // Notify parent of latest scope data
}

const sizeClasses = {
  sm: 'wu-field sm',
  md: 'wu-field md',
  lg: 'wu-field lg',
};

const DragDots = () => (
  <svg viewBox="0 0 16 16" fill="currentColor">
    <circle cx="5" cy="3" r="1.5"/>
    <circle cx="11" cy="3" r="1.5"/>
    <circle cx="5" cy="8" r="1.5"/>
    <circle cx="11" cy="8" r="1.5"/>
    <circle cx="5" cy="13" r="1.5"/>
    <circle cx="11" cy="13" r="1.5"/>
  </svg>
);

const EditableText: React.FC<{
  text: string;
  disabled: boolean;
  onChange: (value: string) => void;
}> = ({ text, disabled, onChange }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const lastText = useRef(text);

  // Sync from props only when the external value actually changed
  useEffect(() => {
    if (spanRef.current && text !== lastText.current) {
      spanRef.current.textContent = text;
      lastText.current = text;
    }
  }, [text]);

  const handleBlur = useCallback(() => {
    const newText = spanRef.current?.textContent || '';
    if (newText !== lastText.current) {
      lastText.current = newText;
      onChange(newText);
    }
  }, [onChange]);

  return (
    <span
      ref={spanRef}
      contentEditable={!disabled}
      suppressContentEditableWarning
      onBlur={handleBlur}
      className="wu-editable-text"
    >
      {text}
    </span>
  );
};

const WriteupRowComponent: React.FC<{
  row: WriteupRow;
  onToggle: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete?: (id: string) => void;
  onFieldChange: (rowId: string, fieldIndex: number, value: string) => void;
  onContentChange: (rowId: string, fieldIndex: number, value: string) => void;
  onDragStart: (e: React.MouseEvent, rowId: string) => void;
}> = ({ row, onToggle, onDuplicate, onFieldChange, onContentChange, onDragStart }) => {
  const isDisabled = !row.enabled;

  return (
    <div className={`wu-row ${row.enabled ? 'checked' : ''}`} data-row-id={row.id}>
      {/* Drag Handle */}
      <div className="wu-drag" onMouseDown={(e) => onDragStart(e, row.id)}>
        <DragDots />
      </div>

      {/* Checkbox */}
      <div className="wu-check">
        <input
          type="checkbox"
          checked={row.enabled}
          onChange={() => onToggle(row.id)}
        />
      </div>

      {/* Content Area */}
      <div className={`wu-content ${isDisabled ? 'dimmed' : ''}`}>
        {row.content.map((item, idx) => {
          if (typeof item === 'string') {
            return (
              <EditableText
                key={idx}
                text={item}
                disabled={isDisabled}
                onChange={(val) => onContentChange(row.id, idx, val)}
              />
            );
          }

          const fieldSize = sizeClasses[item.size];

          // Dropdown field
          if (item.options) {
            return (
              <select
                key={idx}
                value={item.value}
                onChange={(e) => onFieldChange(row.id, idx, e.target.value)}
                disabled={isDisabled}
                className={`${fieldSize} wu-select`}
              >
                <option value="">{item.placeholder || 'Select'}</option>
                {item.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            );
          }

          // Text input field
          return (
            <input
              key={idx}
              type="text"
              inputMode="decimal"
              value={fmtWithCommas(item.value)}
              onChange={(e) => onFieldChange(row.id, idx, stripCommas(e.target.value))}
              disabled={isDisabled}
              placeholder={item.placeholder || '___'}
              className={fieldSize}
            />
          );
        })}
      </div>

      {/* Actions */}
      <div className="wu-actions">
        <button
          onClick={() => onDuplicate(row.id)}
          className="wu-dup-btn"
          title="Duplicate row"
        >
          <Copy size={16} />
        </button>
      </div>
    </div>
  );
};

const WriteupNoteRow: React.FC<{
  note: WriteupNote;
  onNoteChange: (noteId: string, content: string) => void;
  onAddNoteRow?: (noteId: string, text: string) => void;
}> = ({ note, onNoteChange, onAddNoteRow }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(() => note.content.trim().length > 0);
  const hasContent = note.content.trim().length > 0;

  const submitNote = () => {
    if (hasContent && onAddNoteRow) {
      onAddNoteRow(note.id, note.content.trim());
      onNoteChange(note.id, '');
      setActive(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitNote();
    }
    if (e.key === 'Escape') {
      onNoteChange(note.id, '');
      setActive(false);
    }
  };

  const handleCheckboxClick = () => {
    if (!active) {
      // First click: activate the input for typing
      setActive(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    } else if (hasContent) {
      // Already active with content: submit the note
      submitNote();
    }
  };

  return (
    <div className={`wu-note-row ${active ? 'active' : ''}`}>
      <div className="wu-note-check">
        <input
          type="checkbox"
          checked={active}
          onChange={handleCheckboxClick}
          title={active ? (hasContent ? 'Add this note' : 'Type your note, then press Enter') : 'Click to add a note'}
        />
      </div>
      <input
        ref={inputRef}
        type="text"
        value={note.content}
        onChange={(e) => onNoteChange(note.id, e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={submitNote}
        disabled={!active}
        placeholder={active ? 'Type your note...' : 'Check the box to add a note'}
        className={`wu-note-input ${!active ? 'dimmed' : ''}`}
      />
    </div>
  );
};

const WriteupSectionComponent: React.FC<{
  section: WriteupSection;
  onToggle: (sectionId: string) => void;
  onRowToggle: (rowId: string) => void;
  onRowDuplicate: (rowId: string) => void;
  onRowDelete: (rowId: string) => void;
  onRowFieldChange: (rowId: string, fieldIndex: number, value: string) => void;
  onRowContentChange: (rowId: string, fieldIndex: number, value: string) => void;
  onNoteChange: (noteId: string, content: string) => void;
  onAddNoteRow: (noteId: string, text: string) => void;
  onDragStart: (e: React.MouseEvent, rowId: string) => void;
}> = ({
  section,
  onToggle,
  onRowToggle,
  onRowDuplicate,
  onRowDelete,
  onRowFieldChange,
  onRowContentChange,
  onNoteChange,
  onAddNoteRow,
  onDragStart,
}) => {
  return (
    <div className={`wu-section-body ${section.collapsed ? 'collapsed' : ''}`}>
      {/* Section Header */}
      <div
        onClick={() => onToggle(section.id)}
        className="wu-section-head"
      >
        <ChevronDown
          size={16}
          className="wu-section-chevron"
        />
        <span className="wu-section-title">
          {section.title}
        </span>
      </div>

      {/* Section Rows */}
      {!section.collapsed && (
        <>
          {section.rows.map((row) => (
            <WriteupRowComponent
              key={row.id}
              row={row}
              onToggle={onRowToggle}
              onDuplicate={onRowDuplicate}
              onDelete={onRowDelete}
              onFieldChange={onRowFieldChange}
              onContentChange={onRowContentChange}
              onDragStart={onDragStart}
            />
          ))}
          <WriteupNoteRow note={section.note} onNoteChange={onNoteChange} onAddNoteRow={onAddNoteRow} />
        </>
      )}
    </div>
  );
};

const WriteupGroupComponent: React.FC<{
  group: WriteupGroup;
  onGroupToggle: (groupId: string) => void;
  onSectionToggle: (sectionId: string) => void;
  onRowToggle: (rowId: string) => void;
  onRowDuplicate: (rowId: string) => void;
  onRowDelete: (rowId: string) => void;
  onRowFieldChange: (rowId: string, fieldIndex: number, value: string) => void;
  onRowContentChange: (rowId: string, fieldIndex: number, value: string) => void;
  onNoteChange: (noteId: string, content: string) => void;
  onAddNoteRow: (noteId: string, text: string) => void;
  onDragStart: (e: React.MouseEvent, rowId: string) => void;
}> = ({
  group,
  onGroupToggle,
  onSectionToggle,
  onRowToggle,
  onRowDuplicate,
  onRowDelete,
  onRowFieldChange,
  onRowContentChange,
  onNoteChange,
  onAddNoteRow,
  onDragStart,
}) => {
  return (
    <div className={`wu-group ${group.collapsed ? 'collapsed' : ''}`}>
      {/* Group Header */}
      <div
        onClick={() => onGroupToggle(group.id)}
        className="wu-group-head"
      >
        <ChevronDown
          size={18}
          className="wu-group-chevron"
        />
        <span className="wu-group-title">{group.title}</span>
      </div>

      {/* Group Content */}
      {!group.collapsed && (
        <div className="wu-group-body">
          {/* Direct rows (no section) */}
          {group.rows.map((row) => (
            <WriteupRowComponent
              key={row.id}
              row={row}
              onToggle={onRowToggle}
              onDuplicate={onRowDuplicate}
              onDelete={onRowDelete}
              onFieldChange={onRowFieldChange}
              onContentChange={onRowContentChange}
              onDragStart={onDragStart}
            />
          ))}

          {/* Note row for direct rows */}
          {(group.rows.length > 0 || group.note) && (
            <WriteupNoteRow
              note={group.note || { id: `${group.id}-note`, content: '' }}
              onNoteChange={onNoteChange}
              onAddNoteRow={onAddNoteRow}
            />
          )}

          {/* Sub-sections */}
          {group.sections.map((section) => (
            <WriteupSectionComponent
              key={section.id}
              section={section}
              onToggle={onSectionToggle}
              onRowToggle={onRowToggle}
              onRowDuplicate={onRowDuplicate}
              onRowDelete={onRowDelete}
              onRowFieldChange={onRowFieldChange}
              onRowContentChange={onRowContentChange}
              onNoteChange={onNoteChange}
              onAddNoteRow={onAddNoteRow}
              onDragStart={onDragStart}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Default writeup groups per project type ──

function buildAsphaltGroup(): WriteupGroup {
  return {
    id: 'asphalt',
    title: 'Asphalt',
    collapsed: false,
    note: { id: 'asphalt-note', content: '' },
    rows: [
      { id: 'a-r7', enabled: false, content: ['Grade and compact native soil as needed to place base / asphalt in an area approximately ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
      { id: 'a-r2', enabled: false, content: ['Place weed killer prior to placement of base/asphalt material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
      { id: 'a-r8', enabled: false, content: ['Place ground sterilizer before placement of new asphalt.'] },
      { id: 'a-r3', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of 2x4 redwood header board.'] },
      { id: 'a-r4', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of 2x6 redwood header board.'] },
      { id: 'a-r5', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of 2x8 redwood header board.'] },
      { id: 'a-r1', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of class 2 base material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
      { id: 'a-r6', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of asphalt material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
    ],
    sections: [
      {
        id: 'a-sec1', title: 'Remove & Replace', collapsed: false,
        rows: [
          { id: 'a-sr1', enabled: false, content: ['Saw cut ', { value: '', size: 'sm', placeholder: 'length' }, ' Linear feet of concrete.'] },
          { id: 'a-sr2', enabled: false, content: ['Saw cut ', { value: '', size: 'sm', placeholder: 'length' }, ' Linear feet of asphalt.'] },
          { id: 'a-sr4', enabled: false, content: ['Mill out ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches thick of asphalt. Cut, load, haul, and dispose of legally.'] },
          { id: 'a-sr6', enabled: false, content: ['Mill out ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches thick of dirt. Cut, load, haul, and dispose of legally.'] },
          { id: 'a-sr3', enabled: false, content: ['Remove ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches thick of asphalt. Cut, load, haul, and dispose of legally.'] },
          { id: 'a-sr5', enabled: false, content: ['Pulverize ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches in place existing AC to make CMB, Water to Max Optimum, and compact to 95% Prior to placement of new pavement.'] },
          { id: 'a-sr7', enabled: false, content: ['Remove ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches thick of concrete slab. Cut, load, haul, and dispose of legally.'] },
          { id: 'a-sr11', enabled: false, content: ['Grade and compact native soil as needed to place base/asphalt in approximately ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
          { id: 'a-sr12', enabled: false, content: ['Place approx. ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft. SS-1H material along vertical edges of asphalt prior to placement of new material.'] },
          { id: 'a-sr16', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of class 2 base material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
          { id: 'a-sr13', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of PG64-10 asphalt material to pave approx. ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
          { id: 'a-sr14', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of PG64-10 asphalt material to patch back milled cracks totaling approx. ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
        ],
        note: { id: 'a-snote1', content: '' },
      },
      {
        id: 'a-sec3', title: 'Overlay', collapsed: false,
        rows: [
          { id: 'a-or8', enabled: false, content: ['Header cut ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft. at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches depth along the edges of concrete, remove asphalt grindings and dispose of legally.'] },
          { id: 'a-or11', enabled: false, content: ['Mill out ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches thick of asphalt. Cut, load, haul, and dispose of legally.'] },
          { id: 'a-or9', enabled: false, content: ['Clean existing asphalt paving with high-pressure blowers before placement of asphalt.'] },
          { id: 'a-or5', enabled: false, content: ['Clean existing asphalt paving with high-pressure blowers before placement of hot oil.'] },
          { id: 'a-or1', enabled: false, content: ['Place approx ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft. SS-1H material prior to placement of asphalt material.'] },
          { id: 'a-or10', enabled: false, content: ['Place AR8000 hot oil binder prior to placement of Petromat material.'] },
          { id: 'a-or2', enabled: false, content: ['Furnish and place asphalt material to patch areas that need immediate attention in an area approx. ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches deep.'] },
          { id: 'a-or4', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches (+/-) of PG64-10 asphalt material to construct overlays totaling approx. ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
          { id: 'a-or6', enabled: false, content: ['Furnish and place Petromat material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft. and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of asphalt overlay over Petromat.'] },
        ],
        note: { id: 'a-snote3', content: '' },
      },
    ],
  };
}

function buildGradingGroup(): WriteupGroup {
  return {
    id: 'grading',
    title: 'Grading',
    collapsed: false,
    note: { id: 'grading-note', content: '' },
    rows: [
      { id: 'g-r2', enabled: false, content: ['Grade and compact native soil as needed to place base / asphalt in an area approximately ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
      { id: 'g-r3', enabled: false, content: ['Place weed killer prior to placement of base/asphalt material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
      { id: 'g-r5', enabled: false, content: ['Place ground sterilizer before placement of new asphalt.'] },
      { id: 'g-r4', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches (+ or -) of class 2 base material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
    ],
    sections: [],
  };
}

function buildConcreteGroup(): WriteupGroup {
  return {
    id: 'concrete',
    title: 'Concrete',
    collapsed: false,
    note: { id: 'concrete-note', content: '' },
    rows: [
      { id: 'c-r17', enabled: false, content: ['Saw cut ', { value: '', size: 'sm', placeholder: 'length' }, ' Linear feet of concrete.'] },
      { id: 'c-r5', enabled: false, content: ['Remove and Replace materials and various items listed.'] },
      { id: 'c-r18', enabled: false, content: ['Remove ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of curb. Cut, load, haul, and dispose of legally.'] },
      { id: 'c-r20', enabled: false, content: ['Remove ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of rolled curb. Cut, load, haul, and dispose of legally.'] },
      { id: 'c-r19', enabled: false, content: ['Remove ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of curb and gutter. Cut, load, haul, and dispose of legally.'] },
      { id: 'c-r22', enabled: false, content: ['Remove ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of concrete. Haul & dispose.'] },
      { id: 'c-r21', enabled: false, content: ['Remove approximately ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches thick of concrete in ', { value: '', size: 'sm', placeholder: 'qty' }, ' area. Load, cut haul and dispose of legally.'] },
      { id: 'c-r4', enabled: false, content: ['Grade and compact native soil as needed in order to place concrete in area approximately ', { value: '', size: 'md', placeholder: 'area' }, ' square feet.'] },
      { id: 'c-r23', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of class 2 base material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
      { id: 'c-r1', enabled: false, content: ['Set forms to construct concrete.'] },
      { id: 'c-r14', enabled: false, content: ['Install #', { value: '', size: 'sm', placeholder: 'size' }, ' rebar 12" on center totaling approximately ', { value: '', size: 'md', placeholder: 'area' }, ' SF.'] },
      { id: 'c-r15', enabled: false, content: ['Install #', { value: '', size: 'sm', placeholder: 'size' }, ' rebar 18" on center totaling approximately ', { value: '', size: 'md', placeholder: 'area' }, ' SF.'] },
      { id: 'c-r16', enabled: false, content: ['Install #', { value: '', size: 'sm', placeholder: 'size' }, ' rebar 24" on center totaling approximately ', { value: '', size: 'md', placeholder: 'area' }, ' SF.'] },
      { id: 'c-r3', enabled: false, content: ['Furnish and place ', { value: '', size: 'md', placeholder: 'area' }, ' square feet of concrete slabs at a thickness of ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches.'] },
      { id: 'c-r3b', enabled: false, content: ['Furnish and place ', { value: '', size: 'md', placeholder: 'area' }, ' square feet of concrete material for sidewalks.'] },
      { id: 'c-r9', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of CURB ONLY with 6" face 6" footing.'] },
      { id: 'c-r10', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of CURB ONLY with 8" face 12" footing.'] },
      { id: 'c-r6', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of 6" CURB 18" GUTTER.'] },
      { id: 'c-r2', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of 6" CURB 24" GUTTER.'] },
      { id: 'c-r7', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of 8" CURB 18" GUTTER.'] },
      { id: 'c-r8', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of 8" CURB 24" GUTTER.'] },
      { id: 'c-r12', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of Rolled Curb (2.25 feet wide per city standards).'] },
      { id: 'c-r13', enabled: false, content: ['Custom Curb (After selecting this item, navigate to the concrete tab and input the custom curb information).'] },
      { id: 'c-r11', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'length' }, ' linear feet of "V" GUTTER (3\' wide, 6" deep).'] },
    ],
    sections: [],
  };
}

function buildSealCoatGroup(): WriteupGroup {
  return {
    id: 'seal-coat',
    title: 'Seal Coat',
    collapsed: false,
    note: { id: 'seal-coat-note', content: '' },
    rows: [
      // Clean / prep
      { id: 'sc-r1', enabled: false, content: ['Clean existing asphalt paving as needed with high-pressure blowers prior to placement of seal material.'] },
      // Crack fill — hot
      { id: 'sc-r5', enabled: false, content: ['Clean approx. ', { value: '', size: 'md', placeholder: 'length' }, ' Linear ft of cracks ', { value: '', size: 'sm', placeholder: 'depth' }, '" deep and ', { value: '', size: 'sm', placeholder: 'width' }, '" wide, free of debris and vegetation with high-pressure blowers, treat cracks with ground sterilizer, and seal cracks with an ample amount of HOT rubber crack fill material.'] },
      // Crack fill — cold
      { id: 'sc-r4', enabled: false, content: ['Clean approx. ', { value: '', size: 'md', placeholder: 'length' }, ' Linear ft of cracks ', { value: '', size: 'sm', placeholder: 'depth' }, '" deep and ', { value: '', size: 'sm', placeholder: 'width' }, '" wide, free of debris and vegetation with high-pressure blowers, treat cracks with ground sterilizer, and seal cracks with ample COLD pour crack seal material.'] },
      // Crack fill — mastic
      { id: 'sc-r3', enabled: false, content: ['Apply Type One Mastic crack seal material as needed for any cracks found to be more than 1.5" wide. Price as quoted includes up to ', { value: '', size: 'sm', placeholder: 'lineal ft' }, ' lineal feet.'] },
      // Treat oil spots
      { id: 'sc-r6', enabled: false, content: ['Treat oil spots with oil sealer.'] },
      // Apply sealcoat
      { id: 'sc-r7', enabled: false, content: ['Apply ', { value: '', size: 'sm', placeholder: 'coats', options: [{ label: '1', value: '1' }, { label: '2', value: '2' }] }, ' coat(s) of industrial grade seal coat to a ', { value: '', size: 'sm', placeholder: 'surface', options: [{ label: 'Smooth', value: 'Smooth' }, { label: 'Medium', value: 'Medium' }, { label: 'Rough', value: 'Rough' }] }, ' surface with an area of ', { value: '', size: 'md', placeholder: 'area' }, ' square feet.'] },
      // Apply sealcoat w/ 2% latex additive
      { id: 'sc-r8', enabled: false, content: ['Apply ', { value: '', size: 'sm', placeholder: 'coats', options: [{ label: '1', value: '1' }, { label: '2', value: '2' }] }, ' coat(s) of industrial grade seal coat with 2% latex additive to a ', { value: '', size: 'sm', placeholder: 'surface', options: [{ label: 'Smooth', value: 'Smooth' }, { label: 'Medium', value: 'Medium' }, { label: 'Rough', value: 'Rough' }] }, ' surface of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] },
    ],
    sections: [],
  };
}

function buildStripingGroup(): WriteupGroup {
  return {
    id: 'striping',
    title: 'Striping',
    collapsed: false,
    note: { id: 'striping-note', content: '' },
    rows: [],
    sections: [],
  };
}

const PROJECT_TYPE_GROUP_BUILDERS: Record<string, () => WriteupGroup> = {
  'asphalt': buildAsphaltGroup,
  'grading': buildGradingGroup,
  'concrete': buildConcreteGroup,
  'seal-coat': buildSealCoatGroup,
  'striping': buildStripingGroup,
};

function buildGroupsFromProjectTypes(projectTypes: string[]): WriteupGroup[] {
  const groups: WriteupGroup[] = [];
  for (const pt of projectTypes) {
    const builder = PROJECT_TYPE_GROUP_BUILDERS[pt];
    if (builder) groups.push(builder());
  }
  // Fallback: if nothing matched, show asphalt
  if (groups.length === 0) groups.push(buildAsphaltGroup());
  return groups;
}

// Row ids that exist only in legacy saved data (no current default row to merge into,
// and we intentionally drop them on migration).
function isLegacyRowId(id: string): boolean {
  if (id.startsWith('st-')) return true;        // striping legacy pre-built rows
  if (id === 'sc-r2') return true;              // replaced by sc-r5
  return false;
}

// Strip any trailing "-copy-<ts>" suffixes so duplicates resolve to their base id.
function baseRowId(id: string): string {
  return id.replace(/(-copy-\d+)+$/, '');
}

// SAVED ORDER GOVERNS. Rows are drag-reorderable, so the user's sequence is
// user data exactly like their text and values are.
//
// This used to walk defaultRows first and append saved-only rows at the end,
// which rebuilt every group in default order and silently discarded a drag
// reorder — enabled flags and values survived, so it read as "the order didn't
// save" rather than as data loss. The docblock on mergeRowFromSaved already
// promised ordering was preserved; only this function disagreed.
//
// New default rows are spliced in next to whichever default sibling precedes
// them and is already present, so a newly-introduced row still lands somewhere
// sensible instead of always at the bottom. Legacy ids (st-*, sc-r2) are
// dropped. Duplicates ("<base>-copy-<ts>") need no special handling now — they
// already sit in the right place in the saved order.
function mergeRowsFromSaved(defaultRows: WriteupRow[], savedRows: WriteupRow[]): WriteupRow[] {
  const out: WriteupRow[] = [];
  for (const saved of savedRows) {
    if (isLegacyRowId(saved.id)) continue;
    out.push(saved);
  }

  let cursor = 0;
  for (const d of defaultRows) {
    const existing = out.findIndex((r) => r.id === d.id);
    if (existing !== -1) {
      cursor = existing + 1;
      // Keep the user's duplicates glued to their base — otherwise a new
      // default row splices in between "a-r6" and "a-r6-copy-…".
      while (cursor < out.length && baseRowId(out[cursor].id) === d.id) cursor += 1;
      continue;
    }
    out.splice(cursor, 0, d);
    cursor += 1;
  }

  return out;
}

function mergeSectionFromSaved(defaultSection: WriteupSection, savedSection: WriteupSection): WriteupSection {
  return {
    ...defaultSection,
    rows: mergeRowsFromSaved(defaultSection.rows, savedSection.rows),
    note: savedSection.note?.content
      ? { ...defaultSection.note, content: savedSection.note.content }
      : defaultSection.note,
  };
}

function mergeGroupFromSaved(defaultGroup: WriteupGroup, savedGroup: WriteupGroup): WriteupGroup {
  const savedSectionsById = new Map(savedGroup.sections.map((s) => [s.id, s]));
  const newSections = defaultGroup.sections.map((ds) => {
    const ss = savedSectionsById.get(ds.id);
    return ss ? mergeSectionFromSaved(ds, ss) : ds;
  });
  return {
    ...defaultGroup,
    rows: mergeRowsFromSaved(defaultGroup.rows, savedGroup.rows),
    sections: newSections,
    note: savedGroup.note?.content && defaultGroup.note
      ? { ...defaultGroup.note, content: savedGroup.note.content }
      : defaultGroup.note,
  };
}

// Migration: detect old saved data and rebuild from current defaults
// Old data is identified by missing Overlay section in asphalt or missing 2x8 header row
export function migrateGroups(saved: WriteupGroup[]): WriteupGroup[] {
  const needsMigration = saved.some((g) => {
    if (g.id === 'asphalt') {
      const hasOverlay = g.sections.some(s => s.id === 'a-sec3');
      const has2x8 = g.rows.some(r => r.id === 'a-r5');
      // Detect old row IDs from before R&R renumber (a-sr7 was SS-1H, now is concrete slab)
      const rrSection = g.sections.find(s => s.id === 'a-sec1');
      const hasConcreteSlabRow = rrSection?.rows.some(r => r.id === 'a-sr7' && r.content.some(c => typeof c === 'string' && c.includes('concrete slab')));
      const hasSr14 = rrSection?.rows.some(r => r.id === 'a-sr14');
      if (!hasOverlay || !has2x8 || !hasConcreteSlabRow || !hasSr14) return true;
    }
    if (g.id === 'concrete') {
      const hasEV = g.rows.some(r => r.id === 'c-r21');
      const hasSidewalks = g.rows.some(r => r.id === 'c-r3b');
      if (!hasEV || !hasSidewalks) return true;
    }
    if (g.id === 'seal-coat') {
      const hasMastic = g.rows.some(r => r.id === 'sc-r3');
      const hasOilSpots = g.rows.some(r => r.id === 'sc-r6');
      if (!hasMastic || !hasOilSpots) return true;
    }
    if (g.id === 'grading') {
      // Only "is the current default row missing?" belongs here. There used to
      // be a `!g.rows.some(r => r.id === 'g-r1')` clause meant to force old
      // g-r1 out — but the rebuild never removed it (g-r1 isn't a legacy id, so
      // mergeRowsFromSaved re-appends it), leaving needsMigration permanently
      // true. Those estimates rebuilt on EVERY mount, which is how leaving the
      // Writeup tab and returning reset a drag reorder every single time.
      // Dropping the clause is deliberate: g-r1 holds user data and is the
      // user's to delete, not ours.
      const hasR4 = g.rows.some(r => r.id === 'g-r4');
      if (!hasR4) return true;
    }
    if (g.id === 'striping') {
      // Only trigger migration if there are legacy pre-built rows (ids starting with 'st-').
      // User-added note rows (ids starting with 'note-') are valid and must not cause a rebuild.
      if (g.rows.some(r => r.id.startsWith('st-'))) return true;
    }
    return false;
  });

  if (!needsMigration) {
    // Still strip legacy Seal Coat sub-section from Asphalt if present
    // Also upgrade sc-r7 in-place to add dropdown options (preserves user values)
    return saved.map((g) => {
      if (g.id === 'asphalt') {
        let next = g;
        if (next.sections.some(s => s.id === 'a-sec2')) {
          next = { ...next, sections: next.sections.filter(s => s.id !== 'a-sec2') };
        }
        // Insert a-sr16 (class 2 base furnish) before a-sr13 in R&R if missing
        const rrSection2 = next.sections.find(s => s.id === 'a-sec1');
        if (rrSection2 && !rrSection2.rows.some(r => r.id === 'a-sr16')) {
          const sr13Idx = rrSection2.rows.findIndex(r => r.id === 'a-sr13');
          if (sr13Idx >= 0) {
            const newRow: WriteupRow = { id: 'a-sr16', enabled: false, content: ['Furnish and place ', { value: '', size: 'sm', placeholder: 'depth' }, ' inches of class 2 base material in an area of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] };
            const newRows = [...rrSection2.rows.slice(0, sr13Idx), newRow, ...rrSection2.rows.slice(sr13Idx)];
            next = { ...next, sections: next.sections.map(s => s.id === 'a-sec1' ? { ...s, rows: newRows } : s) };
          }
        }
        // Fix a-sr13 and a-or4: old format had [depth, amount, area] fields — drop the
        // 'amount' (# of areas) field which the formula never uses, preserving whichever
        // value is larger as the effective sq ft (max of amount/area to recover confused entries).
        const fixAmountAreaRow = (row: WriteupRow): WriteupRow => {
          const hasOldAmount = row.content.some(c => typeof c === 'object' && (c as WriteupField).placeholder === 'amount');
          if (!hasOldAmount) return row;
          const depthField = row.content.find(c => typeof c === 'object' && (c as WriteupField).placeholder === 'depth') as WriteupField | undefined;
          const amountField = row.content.find(c => typeof c === 'object' && (c as WriteupField).placeholder === 'amount') as WriteupField | undefined;
          const areaField = row.content.find(c => typeof c === 'object' && (c as WriteupField).placeholder === 'area') as WriteupField | undefined;
          const amountVal = parseFloat(amountField?.value || '0');
          const areaVal = parseFloat(areaField?.value || '0');
          const effectiveArea = (!isNaN(amountVal) && amountVal > areaVal) ? String(amountVal) : (areaField?.value || '');
          const mid = row.id === 'a-or4'
            ? ' inches (+/-) of PG64-10 asphalt material to construct overlays totaling approx. '
            : ' inches of PG64-10 asphalt material to pave approx. ';
          return {
            ...row,
            content: [
              'Furnish and place ',
              { ...(depthField || { value: '', size: 'sm' as const }), placeholder: 'depth' },
              mid,
              { value: effectiveArea, size: 'md' as const, placeholder: 'area' },
              ' sq ft.',
            ],
          };
        };
        next = {
          ...next,
          sections: next.sections.map(s => ({
            ...s,
            rows: s.rows.map(r => (r.id === 'a-sr13' || r.id === 'a-or4') ? fixAmountAreaRow(r) : r),
          })),
        };
        return next;
      }
      if (g.id === 'concrete') {
        // Rewrite c-r18/19/20 from "square feet" (area/md) to "linear feet" (length/sm), preserving any entered value
        const curbRemovalIds: Record<string, string> = {
          'c-r18': 'curb',
          'c-r19': 'curb and gutter',
          'c-r20': 'rolled curb',
        };
        let next = { ...g, rows: g.rows.map(r => {
          const kind = curbRemovalIds[r.id];
          if (!kind) return r;
          const hasSquareFeet = r.content.some(c => typeof c === 'string' && c.includes('square feet'));
          if (!hasSquareFeet) return r;
          const existingField = r.content.find(c => typeof c !== 'string') as WriteupField | undefined;
          const preservedValue = existingField?.value ?? '';
          const newContent: (string | WriteupField)[] = ['Remove ', { value: preservedValue, size: 'sm', placeholder: 'length' }, ` linear feet of ${kind}. Cut, load, haul, and dispose of legally.`];
          return { ...r, content: newContent };
        })};
        // Insert c-r22 between c-r20 and c-r21 if missing
        if (!next.rows.some(r => r.id === 'c-r22')) {
          const r20Idx = next.rows.findIndex(r => r.id === 'c-r20');
          if (r20Idx >= 0) {
            const newRow: WriteupRow = { id: 'c-r22', enabled: false, content: ['Remove ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft at ', { value: '', size: 'sm', placeholder: 'depth' }, ' in. of concrete. Haul & dispose.'] };
            next = { ...next, rows: [...next.rows.slice(0, r20Idx + 1), newRow, ...next.rows.slice(r20Idx + 1)] };
          }
        }
        return next;
      }
      if (g.id === 'seal-coat') {
        // Strip legacy sc-r2 (replaced by sc-r5 which takes explicit dimensions).
        // Also strip any pre-existing sc-r8 so we can reinsert the current (2% latex) version.
        const filteredRows = g.rows.filter(r => {
          if (r.id === 'sc-r2') return false;
          if (r.id === 'sc-r8') {
            const hasLatex = r.content.some(c => typeof c === 'string' && c.includes('2% latex'));
            return hasLatex;
          }
          return true;
        });
        let next = { ...g, rows: filteredRows.map(r => {
          if (r.id !== 'sc-r7') return r;
          const hasDropdowns = r.content.some(c => typeof c === 'object' && 'options' in c);
          if (hasDropdowns) return r; // Already upgraded
          // Upgrade: inject options into the first two fields and fix placeholders to lowercase
          let fieldIdx = 0;
          const upgraded = r.content.map(c => {
            if (typeof c !== 'object') return c;
            fieldIdx++;
            if (fieldIdx === 1) return { ...c, placeholder: 'coats', options: [{ label: '1', value: '1' }, { label: '2', value: '2' }] };
            if (fieldIdx === 2) return { ...c, placeholder: 'surface', options: [{ label: 'Smooth', value: 'Smooth' }, { label: 'Medium', value: 'Medium' }, { label: 'Rough', value: 'Rough' }] };
            return c;
          });
          return { ...r, content: upgraded };
        })};
        // Insert sc-r8 (sealcoat w/ 2% latex) after sc-r7 if missing
        if (!next.rows.some(r => r.id === 'sc-r8')) {
          const r7Idx = next.rows.findIndex(r => r.id === 'sc-r7');
          if (r7Idx >= 0) {
            const newRow: WriteupRow = { id: 'sc-r8', enabled: false, content: ['Apply ', { value: '', size: 'sm', placeholder: 'coats', options: [{ label: '1', value: '1' }, { label: '2', value: '2' }] }, ' coat(s) of industrial grade seal coat with 2% latex additive to a ', { value: '', size: 'sm', placeholder: 'surface', options: [{ label: 'Smooth', value: 'Smooth' }, { label: 'Medium', value: 'Medium' }, { label: 'Rough', value: 'Rough' }] }, ' surface of ', { value: '', size: 'md', placeholder: 'area' }, ' sq ft.'] };
            next = { ...next, rows: [...next.rows.slice(0, r7Idx + 1), newRow, ...next.rows.slice(r7Idx + 1)] };
          }
        }
        return next;
      }
      return g;
    }).map(rewriteLoadCutHaul);
  }

  // Non-destructive rebuild: take the current default structure for each saved
  // group, then merge the user's enabled flags, field values, notes, and any
  // user-added rows back on top. Previously this branch threw away all user
  // data by calling buildGroupsFromProjectTypes(groupIds) directly, which is
  // how writeups got wiped when their saved structure was missing a newly-
  // introduced default row (see audit on cmq2jgugf001cjr04ulcmqf1a).
  const savedById = new Map(saved.map((g) => [g.id, g]));
  const groupIds = saved.map((g) => g.id);
  const defaults = buildGroupsFromProjectTypes(groupIds);
  return defaults.map((dg) => {
    const sg = savedById.get(dg.id);
    return sg ? mergeGroupFromSaved(dg, sg) : dg;
  });
}

// Non-destructive text swap: "Load, cut, haul" → "Cut, load, haul" across all saved writeup rows
function rewriteLoadCutHaul(g: WriteupGroup): WriteupGroup {
  const swap = (content: (string | WriteupField)[]) =>
    content.map(c => (typeof c === 'string' && c.includes('Load, cut, haul')) ? c.replace(/Load, cut, haul/g, 'Cut, load, haul') : c);
  const needsSwap = (rows: WriteupRow[]) =>
    rows.some(r => r.content.some(c => typeof c === 'string' && c.includes('Load, cut, haul')));
  const rowsChanged = needsSwap(g.rows);
  const sectionsChanged = g.sections.some(s => needsSwap(s.rows));
  if (!rowsChanged && !sectionsChanged) return g;
  return {
    ...g,
    rows: rowsChanged ? g.rows.map(r => ({ ...r, content: swap(r.content) })) : g.rows,
    sections: sectionsChanged
      ? g.sections.map(s => needsSwap(s.rows) ? { ...s, rows: s.rows.map(r => ({ ...r, content: swap(r.content) })) } : s)
      : g.sections,
  };
}

export function WriteupPanel({ estimateId, projectTypes, initialScopeData, onScopeChange }: WriteupPanelProps) {
  const dragState = useRef<{ active: boolean } | null>(null);
  void dragState; // keep ref for future use

  const [groups, setGroups] = useState<WriteupGroup[]>(() => {
    // If we have saved scope data, restore & migrate it
    if (initialScopeData) {
      try {
        const parsed = JSON.parse(initialScopeData);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Merge saved data with latest builder defaults so new rows appear
          return migrateGroups(parsed);
        }
      } catch { /* fall through to default */ }
    }
    return buildGroupsFromProjectTypes(projectTypes || ['asphalt']);
  });

  // Debounced auto-save of scope data to backend
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);
  const hadInitialData = useRef(!!initialScopeData);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const onScopeChangeRef = useRef(onScopeChange);
  onScopeChangeRef.current = onScopeChange;

  // Flush any pending save immediately (used on unmount)
  const flushSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      const json = JSON.stringify(groupsRef.current);
      estimatesService.update(estimateId, { scopeData: json }).catch((err) => {
        console.error('[WriteupPanel] Failed to flush save on unmount:', err?.response?.data || err?.message || err);
      });
    }
  }, [estimateId]);

  // Flush pending save when component unmounts (tab switch)
  useEffect(() => {
    return () => { flushSave(); };
  }, [flushSave]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const json = JSON.stringify(groups);
      if (!hadInitialData.current) {
        // No saved data — persist the defaults immediately so the
        // proposal preview has something to read even if the user never edits
        onScopeChangeRef.current?.(json);
        estimatesService.update(estimateId, { scopeData: json }).catch(() => {});
      } else if (json !== initialScopeData) {
        // Migration changed the structure (e.g. overlay section added) —
        // push updated scopeData to parent so BackgroundMaterialSync uses
        // the correct data without waiting for user interaction
        onScopeChangeRef.current?.(json);
        estimatesService.update(estimateId, { scopeData: json }).catch(() => {});
      }
      return;
    }
    // Notify parent immediately so material calculations react within 600ms
    // (useMaterialCalculations has its own debounce — no need to delay here)
    const json = JSON.stringify(groups);
    onScopeChangeRef.current?.(json);

    // Debounce only the API save to avoid hammering the backend on every keystroke
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      const saveJson = JSON.stringify(groupsRef.current);
      estimatesService.update(estimateId, { scopeData: saveJson }).catch((err) => {
        console.error('[WriteupPanel] Failed to save scope data:', err?.response?.data || err?.message || err);
      });
    }, 1500); // 1.5s debounce
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [groups, estimateId]);

  // Sync groups when projectTypes change (categories added/removed from tab bar)
  const SECTION_ORDER = ['asphalt', 'concrete', 'grading', 'seal-coat', 'striping'];
  // Initialize to [] so first mount always reconciles projectTypes against the actual
  // groups loaded from initialScopeData (otherwise remounting the panel after adding a
  // category would see prevProjectTypes === projectTypes and skip the add entirely).
  const prevProjectTypes = useRef<string[]>([]);
  useEffect(() => {
    if (!projectTypes || JSON.stringify(projectTypes) === JSON.stringify(prevProjectTypes.current)) return;
    prevProjectTypes.current = projectTypes;

    setGroups((prev) => {
      const currentIds = new Set(prev.map((g) => g.id));
      const targetIds = new Set(projectTypes);
      let changed = false;
      let next = [...prev];

      // Add new groups that don't exist yet
      for (const pt of projectTypes) {
        if (!currentIds.has(pt)) {
          const builder = PROJECT_TYPE_GROUP_BUILDERS[pt];
          if (builder) {
            next.push(builder());
            changed = true;
          }
        }
      }

      // Remove groups that are no longer in projectTypes
      const beforeLen = next.length;
      next = next.filter((g) => targetIds.has(g.id));
      if (next.length !== beforeLen) changed = true;

      if (!changed) return prev;

      // Sort in canonical order
      next.sort((a, b) => {
        const ai = SECTION_ORDER.indexOf(a.id);
        const bi = SECTION_ORDER.indexOf(b.id);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
      return next;
    });
  }, [projectTypes]);

  const toggleGroup = (groupId: string) => {
    setGroups(
      groups.map((g) =>
        g.id === groupId ? { ...g, collapsed: !g.collapsed } : g
      )
    );
  };

  const toggleSection = (sectionId: string) => {
    setGroups(
      groups.map((g) => ({
        ...g,
        sections: g.sections.map((s) =>
          s.id === sectionId ? { ...s, collapsed: !s.collapsed } : s
        ),
      }))
    );
  };

  const toggleRow = (rowId: string) => {
    setGroups(
      groups.map((g) => ({
        ...g,
        rows: g.rows.map((r) =>
          r.id === rowId ? {
            ...r,
            enabled: !r.enabled,
            // Clear field values when unchecking so they're fresh on re-enable
            ...(!r.enabled ? {} : {
              content: r.content.map((item) =>
                typeof item === 'string' ? item : { ...item, value: '' }
              ),
            }),
          } : r
        ),
        sections: g.sections.map((s) => ({
          ...s,
          rows: s.rows.map((r) =>
            r.id === rowId ? {
              ...r,
              enabled: !r.enabled,
              ...(!r.enabled ? {} : {
                content: r.content.map((item) =>
                  typeof item === 'string' ? item : { ...item, value: '' }
                ),
              }),
            } : r
          ),
        })),
      }))
    );
  };

  const duplicateRow = (rowId: string) => {
    // Always derive new IDs from the base registry ID so duplicates of
    // duplicates produce `<base>-copy-<ts>` rather than chained
    // `<base>-copy-<ts>-copy-<ts>...`. The downstream formula registry and
    // H&D mapping look up by base ID.
    const baseId = rowId.replace(/(-copy-\d+)+$/, '');
    const newId = `${baseId}-copy-${Date.now()}`;
    setGroups(
      groups.map((g) => {
        const rowIndex = g.rows.findIndex((r) => r.id === rowId);
        if (rowIndex !== -1) {
          const rowToDuplicate = g.rows[rowIndex];
          const newRow = {
            ...rowToDuplicate,
            id: newId,
          };
          const newRows = [...g.rows];
          newRows.splice(rowIndex + 1, 0, newRow);
          return { ...g, rows: newRows };
        }

        const newSections = g.sections.map((s) => {
          const rowIndex = s.rows.findIndex((r) => r.id === rowId);
          if (rowIndex !== -1) {
            const rowToDuplicate = s.rows[rowIndex];
            const newRow = {
              ...rowToDuplicate,
              id: newId,
            };
            const newRows = [...s.rows];
            newRows.splice(rowIndex + 1, 0, newRow);
            return { ...s, rows: newRows };
          }
          return s;
        });

        return { ...g, sections: newSections };
      })
    );
  };

  const updateRowField = (
    rowId: string,
    fieldIndex: number,
    value: string
  ) => {
    setGroups(
      groups.map((g) => {
        const rowIndex = g.rows.findIndex((r) => r.id === rowId);
        if (rowIndex !== -1) {
          const newRows = [...g.rows];
          const row = newRows[rowIndex];
          const newContent = [...row.content];
          const field = newContent[fieldIndex];
          if (typeof field !== 'string') {
            newContent[fieldIndex] = { ...field, value };
          }
          newRows[rowIndex] = { ...row, content: newContent };
          return { ...g, rows: newRows };
        }

        const newSections = g.sections.map((s) => {
          const rowIndex = s.rows.findIndex((r) => r.id === rowId);
          if (rowIndex !== -1) {
            const newRows = [...s.rows];
            const row = newRows[rowIndex];
            const newContent = [...row.content];
            const field = newContent[fieldIndex];
            if (typeof field !== 'string') {
              newContent[fieldIndex] = { ...field, value };
            }
            newRows[rowIndex] = { ...row, content: newContent };
            return { ...s, rows: newRows };
          }
          return s;
        });

        return { ...g, sections: newSections };
      })
    );
  };

  // Update a string segment in a row's content array
  const updateRowContent = (
    rowId: string,
    fieldIndex: number,
    value: string
  ) => {
    setGroups(
      groups.map((g) => {
        const rowIndex = g.rows.findIndex((r) => r.id === rowId);
        if (rowIndex !== -1) {
          const newRows = [...g.rows];
          const row = newRows[rowIndex];
          const newContent = [...row.content];
          if (typeof newContent[fieldIndex] === 'string') {
            newContent[fieldIndex] = value;
          }
          newRows[rowIndex] = { ...row, content: newContent };
          return { ...g, rows: newRows };
        }

        const newSections = g.sections.map((s) => {
          const rowIndex = s.rows.findIndex((r) => r.id === rowId);
          if (rowIndex !== -1) {
            const newRows = [...s.rows];
            const row = newRows[rowIndex];
            const newContent = [...row.content];
            if (typeof newContent[fieldIndex] === 'string') {
              newContent[fieldIndex] = value;
            }
            newRows[rowIndex] = { ...row, content: newContent };
            return { ...s, rows: newRows };
          }
          return s;
        });

        return { ...g, sections: newSections };
      })
    );
  };

  const updateNote = (noteId: string, content: string) => {
    setGroups((prev) =>
      prev.map((g) => {
        // Group-level note
        if (noteId === `${g.id}-note`) {
          return { ...g, note: { id: noteId, content } };
        }

        // Section-level notes
        const newSections = g.sections.map((s) => {
          if (s.note.id === noteId) {
            return { ...s, note: { ...s.note, content } };
          }
          return s;
        });

        return { ...g, sections: newSections };
      })
    );
  };

  const addNoteAsRow = (noteId: string, text: string) => {
    const newRow: WriteupRow = {
      id: `note-${Date.now()}`,
      content: [text],
      enabled: true,
    };
    setGroups((prev) =>
      prev.map((g) => {
        // Group-level note
        if (noteId === `${g.id}-note`) {
          return { ...g, rows: [...g.rows, newRow] };
        }
        // Section-level notes
        const newSections = g.sections.map((s) => {
          if (s.note.id === noteId) {
            return { ...s, rows: [...s.rows, newRow] };
          }
          return s;
        });
        return { ...g, sections: newSections };
      })
    );
  };

  const deleteRow = (rowId: string) => {
    setGroups((prev) =>
      prev.map((g) => {
        // Try deleting from group-level rows
        const groupIdx = g.rows.findIndex((r) => r.id === rowId);
        if (groupIdx !== -1) {
          return { ...g, rows: g.rows.filter((r) => r.id !== rowId) };
        }
        // Try deleting from section rows
        const newSections = g.sections.map((s) => {
          const sIdx = s.rows.findIndex((r) => r.id === rowId);
          if (sIdx !== -1) {
            return { ...s, rows: s.rows.filter((r) => r.id !== rowId) };
          }
          return s;
        });
        return { ...g, sections: newSections };
      })
    );
  };

  // ── Drag-to-reorder ──
  const handleDragStart = useCallback((e: React.MouseEvent, rowId: string) => {
    e.preventDefault();
    const rowEl = (e.target as HTMLElement).closest('.wu-row') as HTMLElement;
    if (!rowEl) return;

    const rect = rowEl.getBoundingClientRect();

    // Create floating clone
    const clone = rowEl.cloneNode(true) as HTMLElement;
    clone.classList.add('dragging');
    clone.style.width = rect.width + 'px';
    clone.style.left = rect.left + 'px';
    clone.style.top = rect.top + 'px';
    document.body.appendChild(clone);

    // Dim the original row (it stays in place to hold space)
    rowEl.classList.add('drag-ghost');

    const startY = e.clientY;
    let lastSwapId: string | null = null;

    const handleMouseMove = (me: MouseEvent) => {
      // Move clone with cursor
      const dy = me.clientY - startY;
      clone.style.top = (rect.top + dy) + 'px';

      // Find the row the cursor is over (excluding the ghost)
      const container = rowEl.parentElement;
      if (!container) return;
      const rows = Array.from(container.querySelectorAll('.wu-row:not(.drag-ghost)'));

      for (const r of rows) {
        const rr = r.getBoundingClientRect();
        const mid = rr.top + rr.height / 2;
        const targetId = r.getAttribute('data-row-id');
        if (!targetId || targetId === rowId) continue;

        if (me.clientY >= rr.top && me.clientY <= rr.bottom) {
          // Determine direction: if cursor is past the midpoint, swap
          const ghostRect = rowEl.getBoundingClientRect();
          const dragIsAbove = ghostRect.top < rr.top;

          if ((dragIsAbove && me.clientY > mid) || (!dragIsAbove && me.clientY < mid)) {
            if (lastSwapId !== targetId) {
              lastSwapId = targetId;
              reorderRows(rowId, targetId, dragIsAbove);
            }
          }
          break;
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      // Clean up
      clone.remove();
      rowEl.classList.remove('drag-ghost');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [groups]);

  const reorderRows = (dragId: string, targetId: string, insertAfter: boolean) => {
    setGroups(prev => prev.map(g => {
      // Try reordering in group rows
      const dragIdx = g.rows.findIndex(r => r.id === dragId);
      const targetIdx = g.rows.findIndex(r => r.id === targetId);
      if (dragIdx !== -1 && targetIdx !== -1) {
        const newRows = [...g.rows];
        const [dragged] = newRows.splice(dragIdx, 1);
        const newTargetIdx = newRows.findIndex(r => r.id === targetId);
        newRows.splice(insertAfter ? newTargetIdx + 1 : newTargetIdx, 0, dragged);
        return { ...g, rows: newRows };
      }

      // Try reordering in sections
      const newSections = g.sections.map(s => {
        const dragIdx = s.rows.findIndex(r => r.id === dragId);
        const targetIdx = s.rows.findIndex(r => r.id === targetId);
        if (dragIdx !== -1 && targetIdx !== -1) {
          const newRows = [...s.rows];
          const [dragged] = newRows.splice(dragIdx, 1);
          const newTargetIdx = newRows.findIndex(r => r.id === targetId);
          newRows.splice(insertAfter ? newTargetIdx + 1 : newTargetIdx, 0, dragged);
          return { ...s, rows: newRows };
        }
        return s;
      });

      return { ...g, sections: newSections };
    }));
  };

  return (
    <div>
      {groups.map((group) => (
        <WriteupGroupComponent
          key={group.id}
          group={group}
          onGroupToggle={toggleGroup}
          onSectionToggle={toggleSection}
          onRowToggle={toggleRow}
          onRowDuplicate={duplicateRow}
          onRowDelete={deleteRow}
          onRowFieldChange={updateRowField}
          onRowContentChange={updateRowContent}
          onNoteChange={updateNote}
          onAddNoteRow={addNoteAsRow}
          onDragStart={handleDragStart}
        />
      ))}

    </div>
  );
}
